import React, { useEffect, useContext, useCallback, useRef } from "react";
import {
    Route,
    Routes,
    useNavigate,
    useLocation,
    Navigate,
} from "react-router-dom";

import { Security } from "@okta/okta-react";
import { toRelativeUrl } from "@okta/okta-auth-js";
import LogCapture from "./utils/LogManagement/LogHelper";

import { routes, ROUTE_TYPES } from "./routes";

import "./assets/CSS/index.css";
import "./assets/CSS/home.css";

import "./assets/CSS/shared.css";
import "./assets/CSS/sidemenu.css";
import "./assets/CSS/navbar.css";

import "./assets/CSS/users.css";
import "./assets/CSS/users-form.css";
import "./assets/CSS/profile.css";

import "./assets/CSS/model.css";
import "./assets/CSS/modelform.css";

import "./assets/CSS/lab-store.css";
import "./assets/CSS/labstore-product.css";

import "./assets/CSS/scan.css";

// Google Anayltics
import ReactGA from "react-ga";

// CUSTOM Routes
import { SecureRoute } from "./components/Routes/SecureRoute";
import PrivateRoute from "./components/Routes/PrivateRoute";

// USER CONTEXT
import { UserContext } from "./lib/MainContext";
import { DASHBOARD_ROUTES } from "./utils/RoutesConstant";
import SplashScreenView from "./views/Shared/SplashScreenView";

const App = () => {
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    const location = useLocation();
    const {
        oktaClient,
        user: currentUser,
        googleAnalyticsInitialized,
        initUtils,
        isLoggingOut,
    } = useContext(UserContext);

    useEffect(() => {
        initUtils();
        // to ensure having the right pathname
        if (location?.pathname.includes("admin")) {
            window.location.replace(
                `${location?.origin}${DASHBOARD_ROUTES.Overview}`
            );
        }

        oktaClient.start();
        // TODO: To be removed after testing on dev
        oktaClient.tokenManager.on("expired", (key) => {
            LogCapture.info(
                `Token (${key}) is expired for ${currentUser?.email}`
            );
        });

        return () => {
            oktaClient.stop();
            oktaClient.options.restoreOriginalUri = undefined;
        };
    }, []);

    useEffect(() => {
        // Google Analytics
        if (googleAnalyticsInitialized)
            ReactGA.pageview(`${location?.pathname}${location.search}`);
    }, [location, googleAnalyticsInitialized]);

    // Allow for "stale" navigate references since originalUri will be an absolute URL.
    // Using the navigateRef is mainly for preventing OKTA from popping the "Two custom restoreOriginalUri callbacks are detected" error
    const restoreOriginalUri = useCallback((_, originalUri = "/") => {
        const url = toRelativeUrl(originalUri, window.location.origin);
        navigateRef.current(url);
    }, []);

    const customAuthHandler = () => {
        oktaClient?.signInWithRedirect();
    };

    const renderRoutes = () =>
        routes(currentUser).map((route, index) => {
            if (route.isSecured) {
                return (
                    <Route
                        key={`secured-route-${index}`}
                        path={route.path}
                        element={<SecureRoute />}
                    >
                        {renderRoute(route, index)}
                    </Route>
                );
            }

            return renderRoute(route, index);
        });

    const renderRoute = (route, index) => {
        const path = route.isLayout ? "" : route.path;
        return (
            <Route
                key={`sub-route-${index}`}
                path={path}
                exact={route.exact}
                element={route.element}
            >
                {renderSubRoutes(route, index)}
            </Route>
        );
    };

    const renderPrivateRoute = (route, index) => (
        <Route
            key={`private-route-${index}`}
            path={route.path}
            element={
                <PrivateRoute
                    allowedRoles={route.allowedRoles}
                    path={route.path}
                    exact={route.exact}
                    element={route.element}
                    forbidden={route.forbidden}
                />
            }
        >
            {renderPublicRoute(route, index)}
        </Route>
    );

    const renderPublicRoute = (route, index) => (
        <Route
            key={`public-route-${index}`}
            path={route.path}
            exact={route.exact}
            element={route.element}
            index={index}
        />
    );

    const renderSubRoutes = (route) => {
        if (!route.subRoutes) return;

        return route.subRoutes.map((subRoute) =>
            subRoute.type === ROUTE_TYPES.ProtectedRoute
                ? renderPrivateRoute(subRoute, route.index)
                : renderPublicRoute(subRoute, route.index)
        );
    };

    return (
        <>
            {isLoggingOut && <SplashScreenView />}
            <Security
                oktaAuth={oktaClient}
                restoreOriginalUri={restoreOriginalUri}
                onAuthRequired={customAuthHandler}
            >
                <Routes>
                    {renderRoutes()}
                    {/* TODO: Add a smart way to navigate from old routes to new routes */}
                    <Route
                        path="/"
                        index
                        element={<Navigate to={DASHBOARD_ROUTES.Overview} />}
                    />
                    <Route
                        path="*"
                        index
                        element={<Navigate to={DASHBOARD_ROUTES.Overview} />}
                    />
                </Routes>
            </Security>
        </>
    );
};

export default App;
